import { useCallback, useEffect, useRef, useState } from 'react';
import {
  validateEmailAddress,
  validateEmailSyntax,
  type EmailValidationIssue,
  type EmailValidationResult,
} from '../services/emailValidationService';

export type EmailValidationStatus = 'idle' | 'checking' | 'valid' | 'invalid';

interface UseEmailValidationOptions {
  checkMx?: boolean;
  debounceMs?: number;
}

export function useEmailValidation(options: UseEmailValidationOptions = {}) {
  const { checkMx = true, debounceMs = 500 } = options;
  const [status, setStatus] = useState<EmailValidationStatus>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const [issues, setIssues] = useState<EmailValidationIssue[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  const reset = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    requestIdRef.current += 1;
    setStatus('idle');
    setMessage(null);
    setIssues([]);
  }, []);

  const applyResult = useCallback((result: EmailValidationResult) => {
    if (result.valid) {
      setStatus('valid');
      setMessage(null);
      setIssues([]);
      return;
    }
    setStatus('invalid');
    setMessage(result.message);
    setIssues(result.issues);
  }, []);

  const validate = useCallback(
    (email: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }

      const trimmed = email.trim();
      if (!trimmed) {
        reset();
        return;
      }

      const syntaxResult = validateEmailSyntax(trimmed);
      if (!syntaxResult.valid) {
        applyResult(syntaxResult);
        return;
      }

      if (!checkMx) {
        applyResult(syntaxResult);
        return;
      }

      setStatus('checking');
      setMessage(null);
      setIssues([]);

      debounceRef.current = setTimeout(() => {
        const requestId = ++requestIdRef.current;
        void validateEmailAddress(trimmed, { checkMx: true })
          .then((result) => {
            if (requestId !== requestIdRef.current) return;
            applyResult(result);
          })
          .catch(() => {
            if (requestId !== requestIdRef.current) return;
            setStatus('invalid');
            setMessage('Could not verify this email address');
            setIssues(['dns_error']);
          });
      }, debounceMs);
    },
    [applyResult, checkMx, debounceMs, reset]
  );

  const validateNow = useCallback(
    async (email: string): Promise<EmailValidationResult> => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      requestIdRef.current += 1;

      const result = await validateEmailAddress(email, { checkMx });
      applyResult(result);
      return result;
    },
    [applyResult, checkMx]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return {
    status,
    message,
    issues,
    validate,
    validateNow,
    reset,
    isChecking: status === 'checking',
    isValid: status === 'valid',
    isInvalid: status === 'invalid',
  };
}
